import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, Shield, Clock, MapPin, ArrowRight, Phone, Mail, CheckCircle2, MessageCircle, Zap, Award, Car, Map, Globe, Languages, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { faqsData, stepsData } from '@/data/autogoLandingMock';
import { translations } from '@/autogoLandingTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AutoGoMarketingLanding = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('autogoLanguage') || 'es';
  });
  const [formData, setFormData] = useState({
    pickupLocation: '',
    deliveryLocation: '',
    pickupDate: '',
    deliveryDate: '',
    vehicleType: '',
    name: '',
    email: '',
    phone: ''
  });
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});
  
  const t = translations[language];
  const faqs = faqsData[language];
  const steps = stepsData[language];

  // Intersection Observer para animaciones al hacer scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/send-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Error al enviar la solicitud');
      }

      const result = await response.json();
      
      // Track Facebook Pixel event - Lead
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Quote Request',
          content_category: 'Vehicle Transport',
          value: 0,
          currency: 'EUR'
        });
      }
      
      toast.success(t.hero.successTitle, { description: t.hero.successDescription });
      
      // Limpiar el formulario
      setFormData({
        pickupLocation: '',
        deliveryLocation: '',
        pickupDate: '',
        deliveryDate: '',
        vehicleType: '',
        name: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error(t.hero.errorTitle, { description: t.hero.errorDescription });
    }
  };

  const handleWhatsApp = (withFormData = false) => {
    let message = '';
    
    if (withFormData && formData.pickupLocation && formData.deliveryLocation) {
      // Message with form data
      if (language === 'es') {
        message = `Hola! Solicito presupuesto para transporte de vehículo:\n\n` +
                  `📍 Recogida: ${formData.pickupLocation}\n` +
                  `📍 Entrega: ${formData.deliveryLocation}\n` +
                  `📅 Fecha recogida: ${formData.pickupDate || 'A confirmar'}\n` +
                  `📅 Fecha entrega: ${formData.deliveryDate || 'A confirmar'}\n` +
                  `🚗 Tipo de vehículo: ${formData.vehicleType || 'No especificado'}\n\n` +
                  `👤 Nombre: ${formData.name || ''}\n` +
                  `📧 Email: ${formData.email || ''}\n` +
                  `📱 Teléfono: ${formData.phone || ''}\n\n` +
                  `Espero su respuesta. Gracias!`;
      } else {
        message = `Hello! I request a quote for vehicle transport:\n\n` +
                  `📍 Pickup: ${formData.pickupLocation}\n` +
                  `📍 Delivery: ${formData.deliveryLocation}\n` +
                  `📅 Pickup date: ${formData.pickupDate || 'To be confirmed'}\n` +
                  `📅 Delivery date: ${formData.deliveryDate || 'To be confirmed'}\n` +
                  `🚗 Vehicle type: ${formData.vehicleType || 'Not specified'}\n\n` +
                  `👤 Name: ${formData.name || ''}\n` +
                  `📧 Email: ${formData.email || ''}\n` +
                  `📱 Phone: ${formData.phone || ''}\n\n` +
                  `Looking forward to your response. Thanks!`;
      }
    } else {
      // Generic message
      message = language === 'es' 
        ? 'Hola, me gustaría solicitar información sobre el transporte de vehículos'
        : 'Hello, I would like to request information about vehicle transport';
    }
    
    // Track Facebook Pixel event - Contact
    if (window.fbq) {
      window.fbq('track', 'Contact', {
        content_name: 'WhatsApp Click',
        content_category: 'Customer Support'
      });
    }
    
    window.open(`https://wa.me/34930491501?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  const toggleLanguage = () => {
    setLanguage(prev => {
      const newLang = prev === 'es' ? 'en' : 'es';
      localStorage.setItem('autogoLanguage', newLang);
      return newLang;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Promotional Banner */}
      <div className="bg-gradient-to-r from-[#0a3042] via-[#0A4F6E] to-[#0a3042] text-white py-2 px-2 sm:px-4 sticky top-0 z-[60]">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-3">
          <a 
            href="#recuperacion" 
            className="flex items-center gap-1.5 sm:gap-3 hover:opacity-90 transition-opacity group"
          >
            <Award className="w-4 h-4 sm:w-5 sm:h-5 text-[#64ee85] animate-pulse flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base font-semibold text-center leading-tight">
              {language === 'es' 
                ? '🏆 Líderes en Recuperación | 24/7 | Click aquí'
                : '🏆 Vehicle Recovery Leaders | 24/7 | Click here'
              }
            </span>
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-[#64ee85] group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </a>
        </div>
      </div>
      
      {/* Navbar */}
      <nav className="sticky top-[40px] sm:top-[44px] z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 animate-slide-down">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center gap-2 sm:gap-3 animate-fade-in">
              <img 
                src="/car-icon.png" 
                alt="AutoGo" 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl hover:rotate-12 transition-transform duration-300"
              />
              <span className="text-xl sm:text-2xl font-bold text-[#0A4F6E]">AutoGo</span>
            </div>
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a href="#recuperacion" className="text-gray-700 hover:text-[#39FF77] transition-all duration-300 hover:scale-105 text-sm xl:text-base">{t.nav.recovery}</a>
              <a href="#servicios" className="text-gray-700 hover:text-[#39FF77] transition-all duration-300 hover:scale-105 text-sm xl:text-base">{t.nav.services}</a>
              <a href="#como-funciona" className="text-gray-700 hover:text-[#39FF77] transition-all duration-300 hover:scale-105 text-sm xl:text-base">{t.nav.howItWorks}</a>
              <a href="#faq" className="text-gray-700 hover:text-[#39FF77] transition-all duration-300 hover:scale-105 text-sm xl:text-base">{t.nav.faq}</a>
              <a href="#contacto" className="text-gray-700 hover:text-[#39FF77] transition-all duration-300 hover:scale-105 text-sm xl:text-base">{t.nav.contact}</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                aria-label="Change language"
              >
                <Languages className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A4F6E]" />
                <span className="text-xs sm:text-sm font-semibold text-[#0A4F6E] uppercase">{language}</span>
              </button>
              <Button
                type="button"
                variant="outline"
                className="border-2 border-[#0A4F6E] text-[#0A4F6E] bg-white hover:bg-[#0A4F6E]/5 font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 shrink-0"
                onClick={() => navigate('/portal/login')}
              >
                <LogIn className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="hidden sm:inline ml-1">{t.nav.staffLogin}</span>
                <span className="sm:hidden ml-1">{t.nav.staffLoginShort}</span>
              </Button>
              <Button
                type="button"
                className="bg-[#39FF77] text-[#0A4F6E] hover:bg-[#2FD767] hover:scale-105 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5"
                onClick={() => { window.location.href = 'tel:+34930491501'; }}
              >
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t.nav.callNow}</span>
                <span className="sm:hidden">{language === 'es' ? 'Llamar' : 'Call'}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-8 z-50 group">
        <button
          onClick={() => handleWhatsApp(false)}
          className="w-14 h-14 sm:w-16 sm:h-16 bg-[#25D366] text-white rounded-full shadow-2xl hover:scale-110 transition-all duration-300 flex items-center justify-center animate-bounce-slow relative"
          aria-label="Contactar por WhatsApp"
        >
          <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -left-1 sm:-top-2 sm:-left-2 w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded-full animate-ping"></span>
        </button>
        
        {/* Tooltip */}
        <div className="hidden sm:block absolute bottom-20 right-0 bg-white px-4 py-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
          <p className="text-sm font-semibold text-[#0A4F6E]">
            {language === 'es' ? '💬 ¡Chatea con nosotros!' : '💬 Chat with us!'}
          </p>
          <p className="text-xs text-gray-600">
            {language === 'es' ? 'Respuesta inmediata' : 'Immediate response'}
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 lg:pt-36 pb-12 sm:pb-16 px-3 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A4F6E]/5 via-[#39FF77]/5 to-white overflow-hidden relative">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          
          {/* Road Illustration at Bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none">
            {/* Road Base */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0A4F6E]/10 to-transparent">
              {/* Road Lines */}
              <div className="absolute bottom-8 left-0 right-0 flex justify-around items-center px-4">
                <div className="w-16 h-1 bg-gray-300/30 rounded"></div>
                <div className="w-16 h-1 bg-gray-300/30 rounded"></div>
                <div className="w-16 h-1 bg-gray-300/30 rounded"></div>
                <div className="w-16 h-1 bg-gray-300/30 rounded"></div>
                <div className="w-16 h-1 bg-gray-300/30 rounded"></div>
                <div className="w-16 h-1 bg-gray-300/30 rounded"></div>
              </div>
            </div>
            
            {/* Moving Vehicles on Road - Only green vehicles going forward */}
            <div className="absolute bottom-12 -left-40 animate-car-drive-forward">
              <Car className="w-10 h-10 text-[#39FF77]/40" />
            </div>
            <div className="absolute bottom-12 -left-40 animate-car-drive-forward" style={{animationDelay: '4s'}}>
              <Truck className="w-11 h-11 text-[#39FF77]/35" />
            </div>
            <div className="absolute bottom-12 -left-40 animate-car-drive-forward" style={{animationDelay: '8s'}}>
              <Car className="w-9 h-9 text-[#39FF77]/40" />
            </div>
            <div className="absolute bottom-12 -left-40 animate-car-drive-forward" style={{animationDelay: '12s'}}>
              <Truck className="w-10 h-10 text-[#39FF77]/35" />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-4 sm:space-y-6 animate-fade-in-up">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-[#39FF77]/20 to-[#0A4F6E]/20 rounded-full animate-pulse-subtle backdrop-blur-sm border border-[#39FF77]/30">
                <span className="text-[#0A4F6E] font-semibold text-xs sm:text-sm">{t.hero.badge}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A4F6E] leading-tight">
                {t.hero.title} <span className="bg-gradient-to-r from-[#39FF77] via-[#2FD767] to-[#39FF77] bg-[length:200%_auto] animate-gradient-shift text-transparent bg-clip-text">{t.hero.titleHighlight}</span> {t.hero.titleEnd}
              </h1>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                {t.hero.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 animate-fade-in backdrop-blur-sm bg-white/50 px-3 py-2 rounded-lg" style={{animationDelay: '0.1s'}}>
                  <CheckCircle2 className="w-5 h-5 text-[#39FF77]" />
                  <span className="text-gray-700">{t.hero.verified}</span>
                </div>
                <div className="flex items-center gap-2 animate-fade-in backdrop-blur-sm bg-white/50 px-3 py-2 rounded-lg" style={{animationDelay: '0.2s'}}>
                  <CheckCircle2 className="w-5 h-5 text-[#39FF77]" />
                  <span className="text-gray-700">{t.hero.insured}</span>
                </div>
                <div className="flex items-center gap-2 animate-fade-in backdrop-blur-sm bg-white/50 px-3 py-2 rounded-lg" style={{animationDelay: '0.3s'}}>
                  <CheckCircle2 className="w-5 h-5 text-[#39FF77]" />
                  <span className="text-gray-700">{t.hero.delivery}</span>
                </div>
              </div>
            </div>

            {/* Quote Form */}
            <Card className="shadow-2xl border-0 animate-slide-in-right hover:shadow-3xl transition-shadow duration-500 bg-gradient-to-br from-white to-gray-50/50 backdrop-blur-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#39FF77]/10 to-transparent rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tl from-[#0A4F6E]/10 to-transparent rounded-full blur-xl"></div>
              <CardContent className="p-4 sm:p-6 relative z-10">
                <h3 className="text-lg sm:text-xl font-bold text-[#0A4F6E] mb-4 sm:mb-5">{t.hero.formTitle}</h3>
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickupLocation" className="text-gray-700">{t.hero.pickupLocation}</Label>
                    <Input
                      id="pickupLocation"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      placeholder={t.hero.pickupPlaceholder}
                      required
                      className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deliveryLocation" className="text-gray-700">{t.hero.deliveryLocation}</Label>
                    <Input
                      id="deliveryLocation"
                      name="deliveryLocation"
                      value={formData.deliveryLocation}
                      onChange={handleChange}
                      placeholder={t.hero.deliveryPlaceholder}
                      required
                      className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="pickupDate" className="text-gray-700">{t.hero.pickupDate}</Label>
                      <Input
                        id="pickupDate"
                        name="pickupDate"
                        type="date"
                        value={formData.pickupDate}
                        onChange={handleChange}
                        required
                        className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deliveryDate" className="text-gray-700">{t.hero.deliveryDate}</Label>
                      <Input
                        id="deliveryDate"
                        name="deliveryDate"
                        type="date"
                        value={formData.deliveryDate}
                        onChange={handleChange}
                        required
                        className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType" className="text-gray-700">{t.hero.vehicleType}</Label>
                    <Input
                      id="vehicleType"
                      name="vehicleType"
                      value={formData.vehicleType}
                      onChange={handleChange}
                      placeholder={t.hero.vehiclePlaceholder}
                      required
                      className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700">{t.hero.name}</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.hero.namePlaceholder}
                      required
                      className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">{t.hero.email}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.hero.emailPlaceholder}
                      required
                      className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700">{t.hero.phone}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t.hero.phonePlaceholder}
                      required
                      className="border-gray-200 focus:border-[#39FF77] focus:ring-[#39FF77] focus:scale-[1.02] transition-all"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#39FF77] to-[#2FD767] text-[#0A4F6E] hover:from-[#2FD767] hover:to-[#39FF77] hover:scale-105 transition-all duration-300 font-semibold text-base py-5 shadow-lg hover:shadow-2xl hover:shadow-[#39FF77]/50 animate-glow-pulse">
                    {t.hero.submit}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section - Nuevo */}
      <section className="py-12 sm:py-16 lg:py-20 px-3 sm:px-6 lg:px-8 bg-gradient-to-r from-[#0A4F6E] via-[#0A4F6E]/95 to-[#0A4F6E] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSg1NywyNTUsMTE5LDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12 text-center">
            <div className="animate-fade-in-up flex flex-col items-center space-y-4" style={{animationDelay: '0.1s'}}>
              <div className="w-20 h-20 bg-[#39FF77]/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Truck className="w-10 h-10 text-[#39FF77]" />
              </div>
              <div className="text-5xl font-bold text-[#39FF77]">{t.stats.vehicles}</div>
              <div className="text-white/80 text-lg">{t.stats.vehiclesLabel}</div>
            </div>
            <div className="animate-fade-in-up flex flex-col items-center space-y-4" style={{animationDelay: '0.2s'}}>
              <div className="w-20 h-20 bg-[#39FF77]/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Award className="w-10 h-10 text-[#39FF77]" />
              </div>
              <div className="text-5xl font-bold text-[#39FF77]">{t.stats.satisfaction}</div>
              <div className="text-white/80 text-lg">{t.stats.satisfactionLabel}</div>
            </div>
            <div className="animate-fade-in-up flex flex-col items-center space-y-4" style={{animationDelay: '0.3s'}}>
              <div className="w-20 h-20 bg-[#39FF77]/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Clock className="w-10 h-10 text-[#39FF77]" />
              </div>
              <div className="text-5xl font-bold text-[#39FF77]">{t.stats.delivery}</div>
              <div className="text-white/80 text-lg">{t.stats.deliveryLabel}</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="como-funciona" ref={(el) => (sectionRefs.current['como-funciona'] = el)} className={`py-12 sm:py-16 lg:py-20 px-3 sm:px-6 lg:px-8 bg-white ${isVisible['como-funciona'] ? 'animate-fade-in-up' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A4F6E] mb-3 sm:mb-4">{t.howItWorks.title}</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">{t.howItWorks.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <div 
                key={step.id} 
                className="relative group animate-fade-in-up"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="w-20 h-20 bg-[#39FF77] rounded-2xl flex items-center justify-center text-3xl font-bold text-[#0A4F6E] shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    {index + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A4F6E]">{step.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-full h-0.5 bg-gray-200 group-hover:bg-[#39FF77] transition-colors duration-500">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-gray-300 group-hover:text-[#39FF77] transition-colors" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section 
        id="servicios" 
        ref={(el) => (sectionRefs.current['servicios'] = el)}
        className={`py-12 sm:py-16 lg:py-20 px-3 sm:px-6 lg:px-8 bg-gray-50 ${isVisible['servicios'] ? 'animate-fade-in-up' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A4F6E] mb-3 sm:mb-4">{t.services.title}</h2>
            <p className="text-lg sm:text-xl text-gray-600">{t.services.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#39FF77]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Map className="w-8 h-8 text-[#39FF77]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0A4F6E]">{t.services.national}</h3>
                <p className="text-gray-600 leading-relaxed">{t.services.nationalDesc}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#39FF77]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Globe className="w-8 h-8 text-[#39FF77]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0A4F6E]">{t.services.international}</h3>
                <p className="text-gray-600 leading-relaxed">{t.services.internationalDesc}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#39FF77]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Shield className="w-8 h-8 text-[#39FF77]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0A4F6E]">{t.services.fleet}</h3>
                <p className="text-gray-600 leading-relaxed">{t.services.fleetDesc}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Recovery Service - Featured */}
      <section 
        id="recuperacion"
        ref={(el) => (sectionRefs.current['recuperacion'] = el)}
        className={`py-12 sm:py-16 lg:py-20 px-3 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0a3042] to-[#0a3042]/90 relative overflow-hidden ${isVisible['recuperacion'] ? 'animate-fade-in-up' : 'opacity-0'}`}
      >
        {/* Animated pattern background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-24 h-24 border-3 border-[#64ee85] rounded-full animate-pulse-subtle"></div>
          <div className="absolute bottom-10 right-20 w-20 h-20 border-3 border-[#64ee85] rounded-full animate-pulse-subtle" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div className="space-y-4 sm:space-y-5 text-white">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-[#64ee85]/30 to-[#64ee85]/10 rounded-full border-2 border-[#64ee85]/50 animate-pulse-subtle">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-[#64ee85]" />
                <span className="text-[#64ee85] font-bold text-xs sm:text-sm">{t.recovery.badge}</span>
              </div>
              
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-[#64ee85]" />
                  <span className="text-[#64ee85] font-bold text-lg sm:text-xl">{t.recovery.leader}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 leading-tight">
                  {t.recovery.title}
                </h2>
                <div className="w-16 sm:w-20 h-1 bg-[#64ee85] rounded-full"></div>
              </div>

              <p className="text-base sm:text-lg text-white/90 leading-relaxed">
                {t.recovery.description}
              </p>

              <div className="space-y-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Shield className="w-6 h-6 text-[#0a3042]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1 text-[#64ee85]">{t.recovery.service1Title}</h3>
                    <p className="text-white/80 text-sm">{t.recovery.service1Desc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Car className="w-6 h-6 text-[#0a3042]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1 text-[#64ee85]">{t.recovery.service2Title}</h3>
                    <p className="text-white/80 text-sm">{t.recovery.service2Desc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                    <MapPin className="w-6 h-6 text-[#0a3042]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1 text-[#64ee85]">{t.recovery.service3Title}</h3>
                    <p className="text-white/80 text-sm">{t.recovery.service3Desc}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <Button 
                  onClick={() => document.getElementById('pickupLocation').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-[#64ee85] to-[#36e961] text-[#0a3042] hover:from-[#36e961] hover:to-[#64ee85] hover:scale-105 transition-all duration-300 font-bold text-sm sm:text-base px-4 sm:px-6 py-4 sm:py-5 shadow-2xl hover:shadow-[#64ee85]/50 w-full sm:w-auto"
                >
                  {t.recovery.btn1}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </Button>
                <Button 
                  onClick={handleWhatsApp}
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 font-semibold text-sm sm:text-base px-4 sm:px-6 py-4 sm:py-5 w-full sm:w-auto"
                >
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  {t.recovery.btn2}
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-2xl border-3 border-[#64ee85]/30">
                <div className="absolute -top-6 -right-6 w-28 h-28 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-full flex items-center justify-center animate-pulse-subtle shadow-2xl">
                  <Truck className="w-14 h-14 text-[#0a3042]" />
                </div>
                <div className="space-y-5 pt-6">
                  <div>
                    <h3 className="text-2xl font-bold text-[#0a3042] mb-2">{t.recovery.whyTitle}</h3>
                    <div className="w-16 h-1 bg-[#64ee85] rounded-full"></div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-md">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg shadow-lg">
                        1
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0a3042] text-base mb-1">{t.recovery.reason1Title}</h4>
                        <p className="text-gray-600 text-sm">{t.recovery.reason1Desc}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-md">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg shadow-lg">
                        2
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0a3042] text-base mb-1">{t.recovery.reason2Title}</h4>
                        <p className="text-gray-600 text-sm">{t.recovery.reason2Desc}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-md">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg shadow-lg">
                        3
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0a3042] text-base mb-1">{t.recovery.reason3Title}</h4>
                        <p className="text-gray-600 text-sm">{t.recovery.reason3Desc}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-[#64ee85]/20 to-[#0a3042]/20 rounded-lg p-4 border-2 border-[#64ee85]/30">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#64ee85]" />
                        <p className="text-[#0a3042] font-semibold text-sm">{t.recovery.available}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#64ee85]" />
                        <p className="text-[#0a3042] font-semibold text-sm">{t.recovery.allEurope}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#64ee85]" />
                        <p className="text-[#0a3042] font-semibold text-sm">{t.recovery.legal}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#64ee85]" />
                        <p className="text-[#0a3042] font-semibold text-sm">{t.recovery.confidential}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section 
        id="servicios-adicionales"
        ref={(el) => (sectionRefs.current['servicios-adicionales'] = el)}
        className={`py-12 sm:py-16 lg:py-20 px-3 sm:px-6 lg:px-8 bg-white ${isVisible['servicios-adicionales'] ? 'animate-fade-in-up' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <div className="inline-block px-4 sm:px-6 py-1.5 sm:py-2 bg-[#64ee85]/10 rounded-full mb-3 sm:mb-4">
              <span className="text-[#0a3042] font-bold text-sm sm:text-base">{t.additionalServices.badge}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a3042] mb-3 sm:mb-4">{t.additionalServices.title}</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              {t.additionalServices.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-xl flex items-center justify-center mb-4 shadow-md">
                <svg className="w-7 h-7 text-[#0a3042]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0a3042] mb-3">{t.additionalServices.itv}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{t.additionalServices.itvDesc}</p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-xl flex items-center justify-center mb-4 shadow-md">
                <svg className="w-7 h-7 text-[#0a3042]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.5c-1.1 3-2.7 5.5-4 7.5-1.7 2.5-2.5 4.5-2.5 6.5C5.5 19.4 8.4 22 12 22s6.5-2.6 6.5-5.5c0-2-0.8-4-2.5-6.5-1.3-2-2.9-4.5-4-7.5z"/>
                  <path d="M6 8c-0.8 1.5-1.5 2.8-2 4-0.7 1.5-1 2.8-1 4 0 1.9 1.5 3.5 3.5 3.5" opacity="0.5"/>
                  <path d="M18 8c0.8 1.5 1.5 2.8 2 4 0.7 1.5 1 2.8 1 4 0 1.9-1.5 3.5-3.5 3.5" opacity="0.5"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0a3042] mb-3">{t.additionalServices.cleaning}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{t.additionalServices.cleaningDesc}</p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-xl flex items-center justify-center mb-4 shadow-md">
                <svg className="w-7 h-7 text-[#0a3042]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0a3042] mb-3">{t.additionalServices.technical}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{t.additionalServices.technicalDesc}</p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-[#64ee85] to-[#36e961] rounded-xl flex items-center justify-center mb-4 shadow-md">
                <svg className="w-7 h-7 text-[#0a3042]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0a3042] mb-3">{t.additionalServices.representation}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{t.additionalServices.representationDesc}</p>
            </div>
          </div>

          <div className="mt-4 mb-8 text-center">
            <p className="text-gray-600 mb-3 text-lg">{t.additionalServices.question}</p>
            <Button 
              onClick={handleWhatsApp}
              className="bg-gradient-to-r from-[#64ee85] to-[#36e961] text-[#0a3042] hover:from-[#36e961] hover:to-[#64ee85] hover:scale-105 transition-all duration-300 font-bold text-lg px-8 py-6 shadow-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {t.additionalServices.btn}
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq" 
        ref={(el) => (sectionRefs.current['faq'] = el)}
        className={`py-12 sm:py-16 lg:py-20 px-3 sm:px-6 lg:px-8 bg-gradient-to-r from-[#0A4F6E] via-[#0A4F6E]/95 to-[#0A4F6E] relative overflow-hidden ${isVisible['faq'] ? 'animate-fade-in-up' : 'opacity-0'}`}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSg1NywyNTUsMTE5LDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <div className="inline-block px-4 sm:px-6 py-1.5 sm:py-2 bg-[#64ee85]/20 rounded-full mb-3 sm:mb-4">
              <span className="text-white font-bold text-sm sm:text-base">{t.faq.badge}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">{t.faq.title}</h2>
            <p className="text-white/80 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto px-4">
              {t.faq.subtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                className={`bg-white rounded-2xl p-8 cursor-pointer transition-all duration-500 hover:scale-105 hover:shadow-2xl animate-fade-in-up ${
                  selectedFaq === faq.id ? 'ring-4 ring-[#39FF77] shadow-2xl' : 'shadow-lg'
                }`}
                style={{animationDelay: `${index * 0.1}s`}}
                onClick={() => setSelectedFaq(selectedFaq === faq.id ? null : faq.id)}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#39FF77]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    {index === 0 && <Zap className="w-6 h-6 text-[#39FF77]" />}
                    {index === 1 && <Clock className="w-6 h-6 text-[#39FF77]" />}
                    {index === 2 && <Shield className="w-6 h-6 text-[#39FF77]" />}
                    {index === 3 && <Award className="w-6 h-6 text-[#39FF77]" />}
                    {index === 4 && <MapPin className="w-6 h-6 text-[#39FF77]" />}
                    {index === 5 && <Phone className="w-6 h-6 text-[#39FF77]" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0A4F6E] mb-3">{faq.question}</h3>
                    <div className={`overflow-hidden transition-all duration-500 ${
                      selectedFaq === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                    <button className="mt-4 text-[#39FF77] font-semibold hover:underline">
                      {selectedFaq === faq.id ? t.faq.closeBtn : t.faq.openBtn}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-white/80 text-lg mb-4">{t.faq.notFound}</p>
            <Button 
              onClick={handleWhatsApp}
              className="bg-[#39FF77] text-[#0A4F6E] hover:bg-[#2FD767] hover:scale-105 transition-all duration-300 font-semibold text-lg px-8 py-6 shadow-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {t.faq.contactBtn}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="bg-[#0a3042] text-white py-10 sm:py-12 lg:py-16 px-3 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img 
                  src="/car-icon.png" 
                  alt="AutoGo" 
                  className="w-12 h-12 rounded-xl hover:rotate-12 transition-transform"
                />
                <span className="text-2xl font-bold">AutoGo</span>
              </div>
              <p className="text-gray-300 leading-relaxed">
                {t.footer.description}
              </p>
            </div>
            <div className="space-y-6">
              <h3 className="text-xl font-bold">{t.footer.quickLinks}</h3>
              <ul className="space-y-3">
                <li><a href="#recuperacion" className="text-gray-300 hover:text-[#39FF77] transition-colors">{t.nav.recovery}</a></li>
                <li><a href="#servicios" className="text-gray-300 hover:text-[#39FF77] transition-colors">{t.nav.services}</a></li>
                <li><a href="#como-funciona" className="text-gray-300 hover:text-[#39FF77] transition-colors">{t.nav.howItWorks}</a></li>
                <li><a href="#faq" className="text-gray-300 hover:text-[#39FF77] transition-colors">{t.nav.faq}</a></li>
              </ul>
            </div>
            <div className="space-y-6">
              <h3 className="text-xl font-bold">{t.footer.contact}</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 hover:translate-x-2 transition-transform">
                  <Phone className="w-5 h-5 text-[#39FF77]" />
                  <span className="text-gray-300">+34 930 49 15 01</span>
                </div>
                <div className="flex items-center gap-3 hover:translate-x-2 transition-transform">
                  <Mail className="w-5 h-5 text-[#39FF77]" />
                  <span className="text-gray-300">hello@autogodelivery.com</span>
                </div>
                <div className="flex items-center gap-3 hover:translate-x-2 transition-transform">
                  <MessageCircle className="w-5 h-5 text-[#39FF77]" />
                  <button onClick={handleWhatsApp} className="text-gray-300 hover:text-[#39FF77] transition-colors">
                    {t.footer.whatsapp}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 sm:mt-10 lg:mt-12 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-center md:text-left text-sm">{t.footer.rights}</p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link 
                  to="/politica-de-privacidad" 
                  className="text-gray-400 hover:text-[#39FF77] transition-colors text-xs sm:text-sm"
                >
                  {t.footer.privacyPolicy}
                </Link>
                <Link 
                  to="/politica-de-cookies" 
                  className="text-gray-400 hover:text-[#39FF77] transition-colors text-xs sm:text-sm"
                >
                  {t.footer.cookiesPolicy}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AutoGoMarketingLanding;